import { useEffect, useState } from 'react'
import { Link, usePage } from '@inertiajs/react'
import { AppLayout } from '@/components/layout/AppLayout'
import { VocabularyBrainPanel } from '@/components/languages/VocabularyBrainPanel'
import { LanguageHeader } from '@/components/languages/LanguageHeader'
import { CefrFilterBar } from '@/components/languages/CefrFilterBar'
import { ReadingTextList } from '@/components/languages/ReadingTextList'
import { ThemeFilterBar } from '@/components/languages/ThemeFilterBar'
import { fetchLanguage } from '@/api/languages'
import { fetchReadings } from '@/api/readings'
import type { CefrFilter } from '@/components/languages/CefrFilterBar'
import type { Language } from '@/types/language'
import type { Reading } from '@/types/reading'
import type { SharedProps } from '@/types'
import { PageContainer } from '@/components/layout/PageContainer'
import { useCurrentLanguage } from '@/hooks/useCurrentLanguage'
import { matchingReadings, themesIn } from '@/utils/readingThemes'

interface Props {
  id: number
}

export default function LanguagesShow() {
  const { id, auth } = usePage().props as unknown as SharedProps & Props
  useCurrentLanguage(id)
  const isAdmin = auth?.user?.role === 'admin'
  const [language, setLanguage] = useState<Language | null>(null)
  const [readings, setReadings] = useState<Reading[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [languageError, setLanguageError] = useState(false)
  const [filter, setFilter] = useState<CefrFilter>('all')
  const [themes, setThemes] = useState<string[]>([])
  const [query, setQuery] = useState('')

  function toggleTheme(theme: string) {
    setThemes((current) =>
      current.includes(theme) ? current.filter((t) => t !== theme) : [...current, theme],
    )
  }

  useEffect(() => {
    let active = true
    setLoading(true)
    setLanguageError(false)
    Promise.allSettled([fetchLanguage(id), fetchReadings(id)]).then(([lang, reads]) => {
      if (!active) return
      if (lang.status === 'fulfilled') setLanguage(lang.value.data.data)
      else setLanguageError(true)
      if (reads.status === 'fulfilled') setReadings(reads.value.data.data)
      setLoading(false)
    })
    return () => {
      active = false
    }
  }, [id])

  const visible = readings ? matchingReadings(readings, themes, query) : []

  return (
    <AppLayout>
      <PageContainer className="py-12 sm:py-16 lg:py-20">
        {loading && <LanguagePanelSkeleton />}

        {!loading && languageError && !language && (
          <p className="font-serif text-base text-muted" role="alert">
            We could not load this language. Please try again.
          </p>
        )}

        {!loading && language && (
          <div className="animate-enter space-y-12">
            <LanguageHeader language={language} />
            {/* Side by side on wide screens: the brain is a tall canvas, and stacking it
                pushed the catalogue — the reason to visit this page — below the fold. */}
            <div className="space-y-12 xl:grid xl:grid-cols-2 xl:gap-12 xl:space-y-0 xl:items-start">
            <VocabularyBrainPanel languageId={id} />
            {readings === null ? (
              <p className="font-serif text-base text-muted">
                Texts could not be loaded.
              </p>
            ) : (
              <section className="space-y-2">
                {isAdmin && (
                  <div className="flex justify-end">
                    <Link
                      href={`/languages/${id}/readings/new`}
                      className="inline-flex items-center min-h-11 px-3 rounded font-mono text-xs uppercase tracking-widest text-accent hover:bg-surface-bg transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
                    >
                      + Add text
                    </Link>
                  </div>
                )}
                <ThemeFilterBar
                  themes={themesIn(readings)}
                  selected={themes}
                  onToggle={toggleTheme}
                  query={query}
                  onQueryChange={setQuery}
                />
                <CefrFilterBar
                  readings={visible}
                  value={filter}
                  onChange={setFilter}
                />
                <ReadingTextList readings={visible} filter={filter} />
              </section>
            )}
            </div>
          </div>
        )}
      </PageContainer>
    </AppLayout>
  )
}

function LanguagePanelSkeleton() {
  return (
    <div data-testid="language-panel-skeleton" className="space-y-12 animate-pulse">
      <div className="flex items-center gap-4">
        <div className="h-8 w-12 rounded bg-surface-bg" />
        <div className="space-y-2">
          <div className="h-8 w-48 rounded bg-surface-bg" />
          <div className="h-4 w-24 rounded bg-surface-bg" />
        </div>
      </div>
      <div className="h-[clamp(380px,50vw,560px)] rounded-md bg-surface-bg" />
      <div className="space-y-4">
        {[0, 1, 2].map((i) => (
          <div key={i} className="space-y-2">
            <div className="h-5 w-2/3 rounded bg-surface-bg" />
            <div className="h-4 w-full rounded bg-surface-bg" />
          </div>
        ))}
      </div>
    </div>
  )
}
